/**
 * Scoped-export mode.
 *
 * Chapter and section PDF/print exports capture the LIVE interactive DOM
 * (`document.getElementById(sectionId)`), not the dedicated PDF tree. Any
 * chapter that hides content behind an interactive control therefore exports
 * only what happens to be on screen — which is how the Market Evidence Review
 * once exported a single charge card while claiming all charges were reviewed.
 *
 * Components that collapse content subscribe to this flag and render their
 * complete content while a scoped export is in progress.
 */

import { useEffect, useState } from "react";

let exporting = false;
const listeners = new Set<(v: boolean) => void>();

export function isScopedExportActive(): boolean {
  return exporting;
}

export function setScopedExportActive(value: boolean): void {
  if (exporting === value) return;
  exporting = value;
  if (typeof document !== "undefined") {
    if (value) document.documentElement.setAttribute("data-scoped-export", "true");
    else document.documentElement.removeAttribute("data-scoped-export");
  }
  listeners.forEach((fn) => fn(value));
}

/** Runs `fn` with scoped-export mode active, awaiting a paint before capture. */
export async function withScopedExport<T>(fn: () => Promise<T>): Promise<T> {
  setScopedExportActive(true);
  // Let React flush the expanded render before the DOM is captured.
  await new Promise((r) => setTimeout(r, 0));
  await new Promise((r) =>
    typeof requestAnimationFrame === "function" ? requestAnimationFrame(() => r(null)) : r(null),
  );
  try {
    return await fn();
  } finally {
    setScopedExportActive(false);
  }
}

export function useScopedExportActive(): boolean {
  const [active, setActive] = useState(exporting);
  useEffect(() => {
    listeners.add(setActive);
    setActive(exporting);
    return () => {
      listeners.delete(setActive);
    };
  }, []);
  return active;
}
